import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  FeatureApi,
  UpdateFeatureClientMappingPayload,
  UpdateMenuSequencePayload,
} from "../../service/Api/FeatureApi";

export type PendingMappingChange = {
  entityType: "menu" | "feature";
  entityId: number;
  isDeleted: boolean;
  memberType: string[];
  /** Tab where the toggle happened: all | medicare | commercial | aso */
  sourceTabId: string;
};

export type ApplicationFeaturesLoadResult = {
  /** Member types that were requested */
  memberTypes: string[];
  /** Raw API payloads in the same order as memberTypes */
  payloads: any[];
};

const initialState = {
  isLoading: false,
  isMemberFeaturesLoading: false,
  isUpdatingFeatureMapping: false,
  error: null as string | null,
  menusAndFeatures: null as any,
  applicationFeaturesAndMenus: null as ApplicationFeaturesLoadResult | null,
  /** key = `${entityType}:${entityId}:${sourceTabId}` */
  pendingMappingChanges: {} as Record<string, PendingMappingChange>,
};

const changeKey = (
  entityType: "menu" | "feature",
  entityId: number,
  sourceTabId: string,
) => `${entityType}:${entityId}:${sourceTabId}`;

/** memberType decides which saved menu sequence the catalog is returned in */
export const getAllMenusAndFeatures = createAsyncThunk(
  "feature/getAllMenusAndFeatures",
  async (memberType: string | undefined, { rejectWithValue }) => {
    try {
      const response = await FeatureApi.getAllMenusAndFeatures(memberType);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

/**
 * Loads application features for one or more member types.
 * Empty / omitted memberTypes (All tab) fetches once without a memberType query.
 * Pass multiple types to fetch in parallel; UI merges as a union.
 */
export const getApplicationFeaturesAndMenus = createAsyncThunk(
  "feature/getApplicationFeaturesAndMenus",
  async (memberTypes: string | string[] | undefined, { rejectWithValue }) => {
    const types = (
      Array.isArray(memberTypes)
        ? memberTypes
        : memberTypes
          ? [memberTypes]
          : []
    ).filter(Boolean);

    try {
      if (!types.length) {
        const payload = await FeatureApi.getApplicationFeaturesAndMenus();
        return {
          memberTypes: [],
          payloads: [payload],
        } as ApplicationFeaturesLoadResult;
      }

      const payloads = await Promise.all(
        types.map((memberType) =>
          FeatureApi.getApplicationFeaturesAndMenus(memberType),
        ),
      );
      return { memberTypes: types, payloads } as ApplicationFeaturesLoadResult;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateFeatureClientMapping = createAsyncThunk(
  "feature/updateFeatureClientMapping",
  async (data: UpdateFeatureClientMappingPayload, { rejectWithValue }) => {
    try {
      const response = await FeatureApi.updateFeatureClientMapping(data);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateMenuSequence = createAsyncThunk(
  "feature/updateMenuSequence",
  async (data: UpdateMenuSequencePayload, { rejectWithValue }) => {
    try {
      const response = await FeatureApi.updateMenuSequence(data);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const FeatureSlice = createSlice({
  name: "feature",
  initialState,
  reducers: {
    RESET_STATE: () => initialState,
    CLEAR_APPLICATION_FEATURES: (state) => {
      state.applicationFeaturesAndMenus = null;
    },
    UPSERT_PENDING_MAPPING_CHANGE: (
      state,
      action: PayloadAction<PendingMappingChange>,
    ) => {
      const { entityType, entityId, sourceTabId } = action.payload;
      if (Number.isNaN(entityId) || !sourceTabId) return;
      state.pendingMappingChanges[
        changeKey(entityType, entityId, sourceTabId)
      ] = action.payload;
    },
    REMOVE_PENDING_MAPPING_CHANGE: (
      state,
      action: PayloadAction<{
        entityType: "menu" | "feature";
        entityId: number;
        sourceTabId: string;
      }>,
    ) => {
      const { entityType, entityId, sourceTabId } = action.payload;
      delete state.pendingMappingChanges[
        changeKey(entityType, entityId, sourceTabId)
      ];
    },
    CLEAR_PENDING_MAPPING_CHANGES: (state) => {
      state.pendingMappingChanges = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllMenusAndFeatures.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAllMenusAndFeatures.fulfilled, (state, action) => {
        state.isLoading = false;
        state.menusAndFeatures = action.payload;
      })
      .addCase(getAllMenusAndFeatures.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message ?? null;
      })
      .addCase(getApplicationFeaturesAndMenus.pending, (state) => {
        state.isMemberFeaturesLoading = true;
        state.error = null;
      })
      .addCase(getApplicationFeaturesAndMenus.fulfilled, (state, action) => {
        state.isMemberFeaturesLoading = false;
        state.applicationFeaturesAndMenus = action.payload;
      })
      .addCase(getApplicationFeaturesAndMenus.rejected, (state, action) => {
        state.isMemberFeaturesLoading = false;
        state.error = action.error.message ?? null;
      })
      .addCase(updateFeatureClientMapping.pending, (state) => {
        state.isUpdatingFeatureMapping = true;
        state.error = null;
      })
      .addCase(updateFeatureClientMapping.fulfilled, (state) => {
        state.isUpdatingFeatureMapping = false;
      })
      .addCase(updateFeatureClientMapping.rejected, (state, action) => {
        state.isUpdatingFeatureMapping = false;
        state.error = action.error.message ?? null;
      })
      .addCase(updateMenuSequence.pending, (state) => {
        state.isUpdatingFeatureMapping = true;
        state.error = null;
      })
      .addCase(updateMenuSequence.fulfilled, (state) => {
        state.isUpdatingFeatureMapping = false;
      })
      .addCase(updateMenuSequence.rejected, (state, action) => {
        state.isUpdatingFeatureMapping = false;
        state.error = action.error.message ?? null;
      });
  },
});

export const {
  CLEAR_APPLICATION_FEATURES,
  UPSERT_PENDING_MAPPING_CHANGE,
  REMOVE_PENDING_MAPPING_CHANGE,
  CLEAR_PENDING_MAPPING_CHANGES,
} = FeatureSlice.actions;
export default FeatureSlice.reducer;
